import { CSSProperties, useEffect, useRef, useState } from 'react';

import { t } from 'i18next';

import { TaskFragment, useTasksLazyQuery } from '@school_ify/schoolify-repository/src/shared/types';
import { useTaskInsertMutation } from '@school_ify/schoolify-repository/src/student_teacher/types';

import useDeviceDetect from 'hooks/useDeviceDetect';
import Card from 'components/Card/Card';
import TextField from 'clean/components/common/TextField/TextField';
import Button from 'components/Button/Button';
import listIcon from 'assets/icons/header/my-tasks.svg';
import LoadButton from 'components/Button/LoadButton/LoadButton';
import useOnClickOutside from 'hooks/useClickOutside';
import usePagination from 'hooks/usePagination';
import { useAppSelector } from 'redux/hooks';
import { selectUserData } from 'redux/editProfile/userProfile.slices';

import TaskItem from './components/TaskItem/TaksItem';
import EditMyTaskModal from './components/EditMyTaskModal/EditMyTaskModal';

import './MyTasks.scoped.scss';

interface Props {
  children?: React.ReactNode;
  style?: CSSProperties;
}

const Upcoming: React.FC<Props> = ({ style }) => {
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const { hasNext, hasPrev, currentPage, setCurrentPage, setTotalCount, calculateOffset } =
    usePagination();
  const [editTaskModalOpen, setEditTaskModalOpen] = useState(false);
  const [addTaskInputShown, setAddTaskInputShown] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskFragment>();
  const addTaskButtonRef = useRef<HTMLDivElement>(null);
  const userData = useAppSelector(selectUserData);
  const { isMobile } = useDeviceDetect();
  const [showAllTasks, setShowAllTasks] = useState(true);
  const [taskInsertMutation] = useTaskInsertMutation();
  useOnClickOutside(addTaskButtonRef, () => setAddTaskInputShown(false));

  const [loadTasks, { data, refetch }] = useTasksLazyQuery({
    variables: {
      limit: 9,
      offset: 0,
      show_completed: !showAllTasks ? { _eq: false } : {},
    },
  });

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    setTotalCount(data?.tasks_aggregate?.aggregate?.count);
  }, [data]);

  useEffect(() => {
    loadTasks();
    setCurrentPage(1);
  }, [showAllTasks]);

  function queryMore() {
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    loadTasks({
      variables: {
        limit: 9,
        show_completed: !showAllTasks ? { _eq: false } : {},
        offset: calculateOffset(newPage),
      },
    });
  }

  function queryLess() {
    setCurrentPage(1);
    loadTasks({
      variables: {
        limit: 9,
        show_completed: !showAllTasks ? { _eq: false } : {},
        offset: 0,
      },
    });
  }

  function paginate(val) {
    if (val === 'next') {
      queryMore();
      return;
    }
    queryLess();
  }

  const handleNewTaskSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    taskInsertMutation({
      variables: {
        task: { title: newTaskTitle },
      },
      onCompleted() {
        refetch();
      },
    });
    setNewTaskTitle('');
    setAddTaskInputShown(false);
  };

  const handleSelectTask = (task: TaskFragment) => {
    setSelectedTask(task);
    setEditTaskModalOpen(true);
  };

  return (
    <div className="my-tasks-container" style={style}>
      <Card
        title={
          <>
            <img className="card-icon" src={listIcon} alt="bell icon" />
            {t('common:my_tasks')}
          </>
        }
        foldable={isMobile}
      >
        {!isMobile && (
          <Card.Header>
            <img className="card-icon" src={listIcon} alt="bell icon" />
            <h4 className="card-title">{t('common:my_tasks')}</h4>
            {}
            <div>
              {userData?.role !== 'parent' && (
                <Button
                  onClick={() => setAddTaskInputShown(true)}
                  round
                  style={{
                    backgroundColor: '#5CD79E',
                    border: '2px solid #5CD79E',
                    padding: '0px 20px',
                    color: '#fff',
                    margin: '0px 30px',
                    fontSize: '15px',
                    fontWeight: 600,
                    pointerEvents: addTaskInputShown ? 'none' : 'initial',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('common:add_tasks')}
                </Button>
              )}
              {selectedTask && (
                <EditMyTaskModal
                  onClose={() => {
                    setEditTaskModalOpen(false);
                  }}
                  openModal={editTaskModalOpen}
                  task={selectedTask}
                />
              )}
            </div>
            <div className="filler" />
            <div>
              {showAllTasks ? (
                <span className="underline pointer" onClick={() => setShowAllTasks(!showAllTasks)}>
                  {t('common:show_uncompleted')}
                </span>
              ) : (
                <span className="underline pointer" onClick={() => setShowAllTasks(!showAllTasks)}>
                  {t('common:show_all')}
                </span>
              )}
            </div>
          </Card.Header>
        )}
        <Card.Body style={{ padding: '10px 24px' }}>
          <div ref={addTaskButtonRef}>
            {addTaskInputShown && (
              <form onSubmit={e => handleNewTaskSubmit(e)}>
                <TextField
                  onChange={e => setNewTaskTitle(e.target.value)}
                  value={newTaskTitle}
                  placeholder={t('study:title').toString().replace('*', '')}
                />
              </form>
            )}
          </div>
          {data?.tasks &&
            data?.tasks.map((task, id) => {
              return (
                <TaskItem
                  key={`${task.id}-${id}`}
                  refetchData={() => refetch()}
                  task={task}
                  onClick={() => handleSelectTask(task)}
                />
              );
            })}
          <LoadButton hasNext={hasNext} hasPrev={hasPrev} onClick={val => paginate(val)} />
        </Card.Body>
      </Card>
    </div>
  );
};

export default Upcoming;
